import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const MATERIALS = ["Brass", "Bronze", "German Silver", "Wood"];
const SORTS = [
  { v: "featured", l: "Featured" },
  { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "best_selling", l: "Best Selling" },
  { v: "highest_rated", l: "Highest Rated" },
];

export default function Shop() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = sp.get("category") || "";
  const material = sp.get("material") || "";
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "featured";
  const best = sp.get("best");
  const newFlag = sp.get("new");

  useEffect(() => { api.get("/categories").then(({data}) => setCats(data)); }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: {
      category: category || undefined,
      material: material || undefined,
      q: q || undefined,
      sort,
      best_sellers: best ? true : undefined,
      new_arrivals: newFlag ? true : undefined,
      limit: 60,
    } }).then(({data}) => { setItems(data.items); setTotal(data.total); }).finally(() => setLoading(false));
  }, [category, material, q, sort, best, newFlag]);

  const update = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  const activeFilters = (category ? 1 : 0) + (material ? 1 : 0);

  // tidPrefix keeps test ids unique between the desktop sidebar and the mobile drawer.
  const filterControls = (tidPrefix, closeAfter) => (
    <>
      <div>
        <div className="text-sm font-medium mb-3">Category</div>
        <div className="space-y-2 text-sm">
          <button onClick={() => { update("category", ""); closeAfter && setFiltersOpen(false); }} className={`block py-1 ${!category ? "gold-text" : "text-muted-foreground"}`} data-testid={`${tidPrefix}filter-cat-all`}>All</button>
          {cats.map((c) => (
            <button key={c.slug} onClick={() => { update("category", c.slug); closeAfter && setFiltersOpen(false); }} className={`block text-left py-1 ${category===c.slug ? "gold-text" : "text-muted-foreground"} hover:gold-text`} data-testid={`${tidPrefix}filter-cat-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-sm font-medium mb-3">Material</div>
        <div className="space-y-2 text-sm">
          <button onClick={() => { update("material", ""); closeAfter && setFiltersOpen(false); }} className={`block py-1 ${!material ? "gold-text" : "text-muted-foreground"}`}>All</button>
          {MATERIALS.map((m) => (
            <button key={m} onClick={() => { update("material", m); closeAfter && setFiltersOpen(false); }} className={`block text-left py-1 ${material===m ? "gold-text" : "text-muted-foreground"} hover:gold-text`} data-testid={`${tidPrefix}filter-mat-${m}`}>{m}</button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="container-x py-6 md:py-10" data-testid="shop-page">
      <div className="mb-4 md:mb-8 border-b hairline pb-4">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Shop</div>
        <h1 className="font-serif text-3xl md:text-5xl mt-1">{q ? `Results for "${q}"` : category ? cats.find(c=>c.slug===category)?.name || "Shop" : best ? "Best Sellers" : newFlag ? "New Arrivals" : "All Products"}</h1>
      </div>

      {/* Mobile filter + sort bar (products stay immediately visible below) */}
      <div className="md:hidden flex items-center gap-2 mb-4" data-testid="shop-mobile-bar">
        <button
          onClick={() => setFiltersOpen(true)}
          className="flex-1 flex items-center justify-center gap-2 border hairline rounded-full py-2.5 text-sm bg-white"
          data-testid="shop-filter-open-btn"
        >
          <SlidersHorizontal className="w-4 h-4 gold-text" strokeWidth={1.5} />
          Filters{activeFilters > 0 && <span className="bg-[#B8860B] text-ivory text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{activeFilters}</span>}
        </button>
        <select value={sort} onChange={(e) => update("sort", e.target.value)} className="flex-1 border hairline rounded-full px-3 py-2.5 text-sm bg-white" data-testid="shop-sort-mobile">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        {/* FILTERS — desktop sidebar only */}
        <aside className="hidden md:block space-y-6 md:sticky md:top-40 self-start" data-testid="shop-filters">
          {filterControls("", false)}
        </aside>

        {/* PRODUCTS */}
        <div>
          <div className="hidden md:flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground" data-testid="shop-total">{total} products</div>
            <select value={sort} onChange={(e) => update("sort", e.target.value)} className="border hairline rounded-full px-4 py-2 text-sm bg-white" data-testid="shop-sort">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div className="md:hidden text-xs text-muted-foreground mb-3" data-testid="shop-total-mobile">{total} products</div>
          {loading ? <div className="py-20 text-center text-muted-foreground">Loading…</div> :
            items.length === 0 ? (
              <div className="py-20 text-center border hairline rounded-xl" data-testid="empty-shop">
                <div className="font-serif text-2xl mb-2">No products found</div>
                <div className="text-sm text-muted-foreground">Try adjusting your filters.</div>
              </div>
            ) :
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="products-grid">
              {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          }
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filtersOpen && (
        <div className="md:hidden fixed inset-0 z-[100]" data-testid="filter-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-ivory rounded-t-2xl max-h-[75vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b hairline">
              <div className="font-serif text-xl">Filters</div>
              <button onClick={() => setFiltersOpen(false)} aria-label="Close filters" className="p-2.5 -m-1" data-testid="filter-drawer-close">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
              {filterControls("m", true)}
            </div>
            <div className="px-5 py-4 border-t hairline">
              <button onClick={() => setFiltersOpen(false)} className="w-full btn-gold rounded-full py-3 text-xs uppercase tracking-widest" data-testid="filter-drawer-apply">
                Show {total} Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
